/**
 * Completion content for consult tasks.
 * Captures consultation date, provider, summary, and follow-up recommendations.
 * @agentic connections
 */

import { useForm } from "@mantine/form";
import { Alert, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { toDateInputValue } from "@benchmark/ui/utils";
import type { CompletionContentProps } from "./DefaultCompletionContent";

/**
 * Form state for the consult completion wizard.
 * @agentic connections
 */
type ConsultFormData = {
  consultDate: string;
  provider: string;
  summary: string;
  followUp: string;
};

/**
 * Completion form for consult: date, provider, summary, follow-up recommendations.
 * @agentic connections
 */
export const ConsultCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const form = useForm<ConsultFormData>({
    initialValues: {
      consultDate: "",
      provider: "",
      summary: "",
      followUp: "",
    },
    validate: {
      consultDate: (value) => (!value ? "Consultation date is required" : null),
      provider: (value) =>
        !value.trim() ? "Consulting provider is required" : null,
      summary: (value) =>
        !value.trim() ? "Consultation summary is required" : null,
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Capture consultation details",
        requiredFields: ["consultDate", "provider", "summary"],
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [],
  );

  const handleSubmit = useCallback(
    async (values: ConsultFormData) => {
      if (
        !values.consultDate ||
        !values.provider.trim() ||
        !values.summary.trim()
      )
        return;

      setSubmitting(true);
      setError(null);

      try {
        const statusReason = [
          `Consultation on ${values.consultDate} with ${values.provider.trim()}`,
          `Notes: ${values.summary.trim()}`,
          values.followUp.trim() && `Follow-up: ${values.followUp.trim()}`,
        ]
          .filter(Boolean)
          .join(". ");

        await onComplete(statusReason);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
          notes: values.summary.trim() || undefined,
        });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to complete consultation",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [onComplete, teeForTask, task, careMinutes],
  );

  useEffect(() => {
    onDirtyChange?.(
      !!form.values.consultDate ||
        !!form.values.provider.trim() ||
        !!form.values.summary.trim() ||
        !!form.values.followUp.trim() ||
        !!nextTaskOwnerOverride ||
        !!careMinutes,
    );
  }, [form.values, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  const today = toDateInputValue(new Date());

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        submitText="Complete Consultation"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Record the consultation details including date, provider, and
              summary.
            </Text>

            <TextInput
              type="date"
              label="Consultation Date"
              required
              value={form.values.consultDate}
              onChange={(e) =>
                form.setFieldValue("consultDate", e.currentTarget.value)
              }
              error={form.errors.consultDate}
              max={today}
            />

            <TextInput
              label="Consulting Provider"
              placeholder="Enter provider name"
              required
              {...form.getInputProps("provider")}
            />

            <Textarea
              label="Consultation Summary / Notes"
              placeholder="Summary of consultation..."
              required
              {...form.getInputProps("summary")}
              autosize
              minRows={2}
              maxRows={4}
            />

            <Textarea
              label="Follow-up Recommendations (optional)"
              placeholder="Recommendations for follow-up..."
              {...form.getInputProps("followUp")}
              autosize
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
